<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import AiAgentsAPI from 'dashboard/api/aiAgents';
import AiAgentTemplatesAPI from 'dashboard/api/aiAgents/templates';
import AiAgentKnowledgeSourcesAPI from 'dashboard/api/aiAgents/knowledgeSources';
import TemplatePreviewKnowledge from './TemplatePreviewKnowledge.vue';
import { AI_AGENT_ACTIONS } from './constants';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('aiAgents/getUIFlags');
const template = ref(null);
const isLoading = ref(true);
const enabledKnowledgeSourceIds = ref({});

const activeTestTab = ref('chat');
const testMessage = ref('');
const testMessages = ref([]);
const isTesting = ref(false);

const accountAgents = useMapGetter('agents/getVerifiedAgents');
const accountTeams = useMapGetter('teams/getTeams');
const lifecycleStages = useMapGetter('lifecycleStages/getLifecycleStages');

const mockContact = reactive({
  lifecycle_stage_id: '',
  assignee_key: 'unassigned',
  first_name: 'John',
  last_name: 'Doe',
  phone_country_code: '+20',
  phone_number: '+201000000000',
  email: 'test@example.com',
  country: '',
  language: '',
  tags: [],
});

const testConversationState = reactive({
  assignedToHuman: false,
});

const actionTitleByType = computed(() =>
  AI_AGENT_ACTIONS.reduce((acc, action) => {
    acc[action.type] = t(action.titleKey);
    return acc;
  }, {})
);

const enabledActions = computed(() =>
  (template.value?.actions || []).filter(action => action.is_enabled)
);

const actionBadges = computed(() =>
  enabledActions.value.map(
    action => actionTitleByType.value[action.action_type] || action.action_type
  )
);

const canTest = computed(
  () => template.value?.name && template.value?.instructions
);

const agentPayload = computed(() => {
  if (!template.value) return null;

  return {
    emoji: template.value.emoji,
    name: template.value.name.trim(),
    description: template.value.description || '',
    instructions: template.value.instructions,
    provider: template.value.provider,
    model: template.value.model,
    temperature: Number(template.value.temperature),
    handoff_message: template.value.handoff_message || '',
    resolution_message: template.value.resolution_message || '',
    guardrails: template.value.guardrails || [],
    response_guidelines: template.value.response_guidelines || [],
    actions: (template.value.actions || []).map(action => ({
      action_type: action.action_type,
      is_enabled: action.is_enabled,
      instructions: action.instructions || '',
      config: action.config || {},
    })),
  };
});

const lifecycleOptions = computed(() =>
  (lifecycleStages.value || []).map(stage => ({
    value: String(stage.id),
    label: stage.name,
  }))
);

const assigneeOptions = computed(() => {
  const options = [
    {
      value: 'unassigned',
      label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.UNASSIGNED'),
      assignee_type: null,
      assignee_id: null,
    },
  ];

  (accountAgents.value || []).forEach(agent => {
    options.push({
      value: `user:${agent.id}`,
      label: agent.name,
      assignee_type: 'User',
      assignee_id: agent.id,
    });
  });

  (accountTeams.value || []).forEach(team => {
    options.push({
      value: `team:${team.id}`,
      label: team.name,
      assignee_type: 'Team',
      assignee_id: team.id,
    });
  });

  return options;
});

const selectedAssignee = computed(() =>
  assigneeOptions.value.find(
    option => option.value === mockContact.assignee_key
  )
);

const mockContactPayload = computed(() => {
  const lifecycleStage = lifecycleOptions.value.find(
    option => option.value === String(mockContact.lifecycle_stage_id)
  );
  const assignee = selectedAssignee.value;

  return {
    name: `${mockContact.first_name} ${mockContact.last_name}`.trim(),
    first_name: mockContact.first_name,
    last_name: mockContact.last_name,
    email: mockContact.email,
    phone_number: mockContact.phone_number,
    lifecycle_stage: lifecycleStage?.label || '',
    lifecycle_stage_id: mockContact.lifecycle_stage_id || null,
    assignee:
      assignee?.label ||
      t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.UNASSIGNED'),
    assignee_type: assignee?.assignee_type || null,
    assignee_id: assignee?.assignee_id || null,
    assignee_name: assignee?.label || null,
    country: mockContact.country,
    language: mockContact.language,
    tags: mockContact.tags,
  };
});

const formatExecutedAction = action => {
  const assignee = action?.result?.assignee;
  if (assignee?.assignee_name) {
    return `${action.type}: ${assignee.assignee_name}`;
  }
  return action.type;
};

const applyMockContactFromResponse = mockContactResponse => {
  if (!mockContactResponse) return;

  if (mockContactResponse.lifecycle_stage_id) {
    mockContact.lifecycle_stage_id = String(
      mockContactResponse.lifecycle_stage_id
    );
  }

  if (['User', 'Team'].includes(mockContactResponse.assignee_type)) {
    testConversationState.assignedToHuman = true;
    mockContact.assignee_key =
      mockContactResponse.assignee_type === 'User'
        ? `user:${mockContactResponse.assignee_id}`
        : `team:${mockContactResponse.assignee_id}`;
  }
};

const applyTestActionsExecuted = actionsExecuted => {
  (actionsExecuted || []).forEach(action => {
    const assignee = action?.result?.assignee;
    if (!assignee) return;

    if (['User', 'Team'].includes(assignee.assignee_type)) {
      testConversationState.assignedToHuman = true;
      mockContact.assignee_key =
        assignee.assignee_type === 'User'
          ? `user:${assignee.assignee_id}`
          : `team:${assignee.assignee_id}`;
    } else if (assignee.handed_off) {
      testConversationState.assignedToHuman = true;
      mockContact.assignee_key = 'unassigned';
    }
  });
};

const resetTestChat = () => {
  testMessages.value = [];
  testMessage.value = '';
  testConversationState.assignedToHuman = false;
  mockContact.assignee_key = 'unassigned';
};

const sendTestMessage = async () => {
  const content = testMessage.value.trim();
  if (!content || !canTest.value || isTesting.value) return;

  if (testConversationState.assignedToHuman) {
    useAlert(t('AI_AGENTS.TEST_PANEL.ASSIGNED_STOP'));
    return;
  }

  testMessages.value.push({
    id: `user-${Date.now()}`,
    role: 'user',
    content,
  });
  testMessage.value = '';

  try {
    isTesting.value = true;
    const response = await AiAgentsAPI.test({
      ai_agent: agentPayload.value,
      messages: testMessages.value.map(({ role, content: message }) => ({
        role,
        content: message,
      })),
      mock_contact: mockContactPayload.value,
    });

    applyTestActionsExecuted(response.data.actions_executed);
    applyMockContactFromResponse(response.data.mock_contact);

    const reply =
      response.data.reply ||
      (testConversationState.assignedToHuman
        ? t('AI_AGENTS.TEST_PANEL.ASSIGNED_REPLY_STOP')
        : t('AI_AGENTS.TEST_PANEL.EMPTY_REPLY'));

    testMessages.value.push({
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: reply,
      actions: (response.data.actions_executed || []).map(formatExecutedAction),
    });
  } catch {
    useAlert(t('AI_AGENTS.API.ERROR'));
  } finally {
    isTesting.value = false;
  }
};

const applyKnowledgeLinks = async agentId => {
  const entries = Object.entries(enabledKnowledgeSourceIds.value).filter(
    ([, enabled]) => enabled
  );

  await Promise.all(
    entries.map(([sourceId]) =>
      AiAgentKnowledgeSourcesAPI.toggle(agentId, sourceId, true)
    )
  );
};

const publishAgent = async () => {
  if (!agentPayload.value) return;

  try {
    const agent = await store.dispatch('aiAgents/create', agentPayload.value);
    if (!agent) return;

    await applyKnowledgeLinks(agent.id);
    await store.dispatch('aiAgents/publish', agent.id);
    useAlert(t('AI_AGENTS.API.PUBLISH_SUCCESS'));
    router.push({ name: 'ai_agents' });
  } catch {
    useAlert(t('AI_AGENTS.API.ERROR'));
  }
};

const openEdit = () => {
  router.push({
    name: 'ai_agents_new',
    query: { templateSlug: route.params.slug },
  });
};

const goBack = () => {
  router.push({ name: 'ai_agents_templates' });
};

const loadTemplate = async () => {
  try {
    isLoading.value = true;
    const { data } = await AiAgentTemplatesAPI.show(route.params.slug);
    template.value = data;
  } catch {
    useAlert(t('AI_AGENTS.TEMPLATES_PAGE.LOAD_ERROR'));
    router.push({ name: 'ai_agents_templates' });
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('lifecycleStages/get');
  loadTemplate();
});

const isPublishing = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isPublishing
);
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <header
      class="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-n-weak bg-n-background px-4 py-3 md:px-8"
    >
      <div class="flex items-center min-w-0 gap-3">
        <Button
          icon="i-lucide-arrow-left"
          slate
          ghost
          size="sm"
          :title="t('AI_AGENTS.ACTIONS.BACK')"
          @click="goBack"
        />
        <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
          {{ t('AI_AGENTS.FORM.CREATE_TITLE') }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('AI_AGENTS.ACTIONS.CANCEL')"
          slate
          faded
          size="sm"
          @click="goBack"
        />
        <Button
          :label="t('AI_AGENTS.ACTIONS.PUBLISH')"
          size="sm"
          :disabled="!canTest"
          :is-loading="isPublishing"
          @click="publishAgent"
        />
      </div>
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center p-8 text-sm text-n-slate-11"
    >
      {{ t('AI_AGENTS.TEMPLATES_PAGE.LOADING') }}
    </div>

    <div
      v-else-if="template"
      class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
    >
      <div
        class="mx-auto grid w-full min-w-0 max-w-[1600px] items-start gap-6 p-4 md:gap-8 md:p-8 xl:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_26rem]"
      >
        <section class="grid min-w-0 gap-8">
          <article class="grid gap-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="flex items-start gap-3 min-w-0">
                <span class="text-3xl shrink-0" aria-hidden="true">{{
                  template.emoji
                }}</span>
                <div class="min-w-0">
                  <h2 class="m-0 text-xl font-semibold text-n-slate-12">
                    {{ template.name }}
                  </h2>
                  <p
                    v-if="template.description"
                    class="m-0 mt-2 text-sm text-n-slate-11"
                  >
                    {{ template.description }}
                  </p>
                </div>
              </div>
              <Button
                :label="t('AI_AGENTS.TEMPLATE_PREVIEW.EDIT')"
                icon="i-lucide-pencil"
                slate
                faded
                size="sm"
                @click="openEdit"
              />
            </div>

            <div v-if="template.setup_tips?.length" class="grid gap-2">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.TEMPLATE_PREVIEW.SETUP_TIPS') }}
              </h3>
              <ul class="grid gap-1 pl-5 m-0 text-sm list-disc text-n-slate-11">
                <li v-for="(tip, index) in template.setup_tips" :key="index">
                  {{ tip }}
                </li>
              </ul>
            </div>

            <div v-if="actionBadges.length" class="grid gap-2">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.ACTIONS_SECTION.TITLE') }}
              </h3>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="badge in actionBadges"
                  :key="badge"
                  class="rounded-md bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
                >
                  {{ badge }}
                </span>
              </div>
            </div>
          </article>

          <article class="grid min-w-0 gap-5 pb-4">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.KNOWLEDGE.TITLE') }}
            </h2>
            <div
              class="grid gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-2"
            >
              <TemplatePreviewKnowledge
                v-model:enabled-source-ids="enabledKnowledgeSourceIds"
              />
            </div>
          </article>
        </section>

        <aside
          class="flex min-h-[28rem] min-w-0 flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 xl:sticky xl:top-6 xl:max-h-[calc(100vh-5.5rem)] xl:self-start"
        >
          <div class="flex min-h-0 flex-1 flex-col">
            <div class="flex items-center justify-between gap-3 p-4">
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.TEST_PANEL.TITLE') }}
              </h2>
              <Button
                :label="t('AI_AGENTS.TEST_PANEL.RESET_CHAT')"
                icon="i-lucide-rotate-ccw"
                slate
                ghost
                size="sm"
                :disabled="testMessages.length === 0"
                @click="resetTestChat"
              />
            </div>
            <div class="flex gap-4 px-4 border-b border-n-weak">
              <button
                type="button"
                class="px-1 pb-3 text-sm font-medium border-b-2"
                :class="
                  activeTestTab === 'chat'
                    ? 'text-n-brand border-n-brand'
                    : 'text-n-slate-11 border-transparent'
                "
                @click="activeTestTab = 'chat'"
              >
                {{ t('AI_AGENTS.TEST_PANEL.CHAT_TAB') }}
              </button>
              <button
                type="button"
                class="px-1 pb-3 text-sm font-medium border-b-2"
                :class="
                  activeTestTab === 'contact_fields'
                    ? 'text-n-brand border-n-brand'
                    : 'text-n-slate-11 border-transparent'
                "
                @click="activeTestTab = 'contact_fields'"
              >
                {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS_TAB') }}
              </button>
            </div>

            <div
              v-if="activeTestTab === 'chat'"
              class="flex flex-col min-h-0 grow"
            >
              <div
                class="grid content-center flex-1 min-h-0 gap-4 p-4 overflow-y-auto"
              >
                <div
                  v-if="testMessages.length === 0"
                  class="grid justify-items-center gap-2 text-center"
                >
                  <h3 class="m-0 text-base font-semibold text-n-slate-12">
                    {{ t('AI_AGENTS.TEST_PANEL.EMPTY_TITLE') }}
                  </h3>
                  <p class="max-w-sm m-0 text-sm text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.EMPTY_DESCRIPTION') }}
                  </p>
                </div>
                <div v-else class="grid content-start gap-3">
                  <div
                    v-for="message in testMessages"
                    :key="message.id"
                    class="grid gap-2"
                    :class="message.role === 'user' ? 'justify-items-end' : ''"
                  >
                    <p
                      class="max-w-[80%] px-3 py-2 m-0 text-sm rounded-lg"
                      :class="
                        message.role === 'user'
                          ? 'bg-n-brand text-white'
                          : 'bg-n-alpha-2 text-n-slate-12'
                      "
                    >
                      {{ message.content }}
                    </p>
                  </div>
                </div>
              </div>

              <form
                class="grid gap-3 p-4 border-t border-n-weak"
                @submit.prevent="sendTestMessage"
              >
                <textarea
                  v-model="testMessage"
                  rows="3"
                  class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 p-3 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10"
                  :placeholder="t('AI_AGENTS.TEST_PANEL.MESSAGE_PLACEHOLDER')"
                  :disabled="!canTest || testConversationState.assignedToHuman"
                />
                <div class="flex justify-end">
                  <Button
                    icon="i-lucide-send-horizontal"
                    size="sm"
                    type="submit"
                    :disabled="
                      !canTest ||
                      !testMessage.trim() ||
                      isTesting ||
                      testConversationState.assignedToHuman
                    "
                    :is-loading="isTesting"
                  />
                </div>
                <p class="m-0 text-xs text-center text-n-slate-10">
                  {{ t('AI_AGENTS.TEST_PANEL.FOOTER') }}
                </p>
              </form>
            </div>

            <div v-else class="flex flex-col min-h-0 gap-4 p-4 overflow-y-auto">
              <label class="grid gap-1">
                <span class="text-sm font-medium text-n-slate-11">
                  {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.LIFECYCLE') }}
                </span>
                <select
                  v-model="mockContact.lifecycle_stage_id"
                  class="h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
                >
                  <option value="">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_LIFECYCLE') }}
                  </option>
                  <option
                    v-for="option in lifecycleOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="grid gap-1">
                <span class="text-sm font-medium text-n-slate-11">
                  {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ASSIGNEE') }}
                </span>
                <select
                  v-model="mockContact.assignee_key"
                  class="h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
                >
                  <option
                    v-for="option in assigneeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>
